import React, { useEffect, useMemo, useState } from 'react'

// -- types matching backend JSON ---------------------------------------------

type Now = { state: string, updatedAtIso: string, source: string, freshnessMinutes: number }
type DailyScore = { date: string, score: number }
type SleepWeek = { days: { date: string, total_sleep_minutes: number }[], weeklyTotals: { total_sleep_minutes: number }, weeklyAverages: { total_sleep_minutes: number } }
type SleepNight = {
  day: string, bedtimeStart: string, bedtimeEnd: string,
  totalSleepMinutes: number, deepSleepMinutes: number, lightSleepMinutes: number, remSleepMinutes: number,
  awakeMinutes: number, timeInBedMinutes: number, efficiency: number, latencyMinutes: number,
  averageHeartRate: number, lowestHeartRate: number, averageHrv: number, averageBreath: number,
}
type ActivityDay = {
  date: string, score: number, steps: number, activeCalories: number, totalCalories: number,
  targetCalories: number, targetMeters: number, equivalentWalkingMeters: number,
  restingMinutes: number, sedentaryMinutes: number, lowActivityMinutes: number,
  mediumActivityMinutes: number, highActivityMinutes: number, inactivityAlerts: number,
}
type NightlyHR = { day: string, lowestHeartRate: number, averageHeartRate: number, averageHrv: number }
type HR = { time: string, bpm: number }
type BodyOverview = { day: string, age: number, vascularAge: number, pulseWaveVelocity: number, resilienceLevel: string, breathingDisturbanceIndex: number }
type Workout = { day: string, activity: string, intensity: string, calories: number, distanceMeters: number, durationSeconds: number, startIso: string, endIso: string, label: string, source: string }

type Status<T> = { kind: 'loading' } | { kind: 'error' } | { kind: 'ok', data: T }

function useEndpoint<T>(url: string): Status<T>{
  const [s, setS] = useState<Status<T>>({ kind: 'loading' })
  useEffect(() => {
    let cancelled = false
    fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((d: T) => { if (!cancelled) setS({ kind: 'ok', data: d }) })
      .catch(() => { if (!cancelled) setS({ kind: 'error' }) })
    return () => { cancelled = true }
  }, [url])
  return s
}

// -- page --------------------------------------------------------------------

export default function Metrics(){
  const now = useEndpoint<Now>('/api/stress/now')
  const readiness = useEndpoint<DailyScore[]>('/api/readiness/week')
  const sleepScore = useEndpoint<DailyScore[]>('/api/sleep/score')
  const sleepNight = useEndpoint<SleepNight>('/api/sleep/night')
  const sleepWeek = useEndpoint<SleepWeek>('/api/sleep/week')
  const activityDay = useEndpoint<ActivityDay>('/api/activity/day')
  const activityWeek = useEndpoint<ActivityDay[]>('/api/activity/week')
  const nightlyHR = useEndpoint<NightlyHR[]>('/api/heart/nightly')
  const hrDay = useEndpoint<HR[]>('/api/heartrate/day')
  const body = useEndpoint<BodyOverview>('/api/body/overview')
  const workouts = useEndpoint<Workout[]>('/api/workouts/recent')

  return (
    <div className="stack-lg">
      <header>
        <h1 className="h1">Health metrics</h1>
        <p className="muted" style={{ margin: '4px 0 0', fontSize: '.9rem' }}>Live data from my Oura ring.</p>
      </header>

      {/* --- hero: three score tiles + stress state chip row ------------- */}
      <section className="grid grid-3" aria-label="Daily scores">
        <ScoreTile eyebrow="Readiness" trend={readiness} latest={latestScore(readiness)} />
        <ScoreTile eyebrow="Sleep" trend={sleepScore} latest={latestScore(sleepScore)}
                   extra={sleepNight.kind === 'ok' ? `${fmtHM(sleepNight.data.totalSleepMinutes)} last night` : undefined} />
        <ScoreTile eyebrow="Activity" trend={activityWeekScoreSeries(activityWeek)} latest={latestActivityScore(activityWeek)}
                   extra={activityDay.kind === 'ok' ? `${activityDay.data.steps.toLocaleString()} steps` : undefined} />
      </section>

      <section className="card" aria-label="Stress right now">
        <Ready s={now} loading={<SkeletonLines lines={2} />}>
          {n => {
            const label = n.state === 'unknown' ? 'no data yet' : n.state
            return (
              <div className="hstack" style={{ justifyContent: 'space-between' }}>
                <div className="hstack">
                  <StateChip state={n.state} />
                  <div>
                    <div className="muted" style={{ fontSize: '.72rem', letterSpacing: '.05em', textTransform: 'uppercase' }}>Current stress</div>
                    <div style={{ textTransform: 'capitalize', fontSize: '1.05rem' }}>{label}</div>
                  </div>
                </div>
                <div className="muted" style={{ textAlign: 'right', fontSize: '.78rem' }}>
                  <div>Updated {new Date(n.updatedAtIso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  <div>source: {n.source}</div>
                </div>
              </div>
            )
          }}
        </Ready>
      </section>

      {/* --- sleep section ---------------------------------------------- */}
      <section className="card" aria-label="Sleep">
        <div className="section-head">
          <h3>Sleep</h3>
          <span className="sub">{sleepNight.kind === 'ok' ? formatShortDate(sleepNight.data.day) + ' — last night' : ''}</span>
        </div>
        <Ready s={sleepNight} loading={<SkeletonLines lines={3} />}>
          {n => (
            <>
              <div className="stat-row" style={{ marginBottom: 14 }}>
                <div className="stat">
                  <span className="k">Total</span>
                  <span className="v">{fmtHM(n.totalSleepMinutes)}</span>
                </div>
                <div className="stat">
                  <span className="k">Efficiency</span>
                  <span className="v">{n.efficiency}<small>%</small></span>
                </div>
                <div className="stat">
                  <span className="k">Bedtime</span>
                  <span className="v" style={{ fontSize: '.9rem' }}>
                    {formatTime(n.bedtimeStart)} → {formatTime(n.bedtimeEnd)}
                  </span>
                </div>
                <div className="stat">
                  <span className="k">Latency</span>
                  <span className="v">{n.latencyMinutes}<small>m</small></span>
                </div>
              </div>
              <StageBar night={n} />
            </>
          )}
        </Ready>

        <div style={{ marginTop: 18 }}>
          <div className="muted" style={{ fontSize: '.78rem', marginBottom: 6 }}>Last 7 nights</div>
          <Ready s={sleepWeek} loading={<SkeletonBars rows={7} />}>
            {sl => sl.days.length === 0
              ? <span className="muted">No data</span>
              : <SleepWeekList days={sl.days} avgMinutes={sl.weeklyAverages.total_sleep_minutes} />
            }
          </Ready>
        </div>
      </section>

      {/* --- activity section ------------------------------------------- */}
      <section className="card" aria-label="Activity">
        <div className="section-head">
          <h3>Activity</h3>
          <span className="sub">{activityDay.kind === 'ok' ? formatShortDate(activityDay.data.date) : ''}</span>
        </div>
        <Ready s={activityDay} loading={<SkeletonLines lines={3} />}>
          {a => (
            <>
              <div className="stat-row" style={{ marginBottom: 14 }}>
                <div className="stat">
                  <span className="k">Steps</span>
                  <span className="v">{a.steps.toLocaleString()}</span>
                </div>
                <div className="stat">
                  <span className="k">Active cal</span>
                  <span className="v">{a.activeCalories}<small>/ {a.targetCalories} target</small></span>
                </div>
                <div className="stat">
                  <span className="k">Total cal</span>
                  <span className="v">{a.totalCalories.toLocaleString()}</span>
                </div>
                <div className="stat">
                  <span className="k">Walk dist</span>
                  <span className="v">{(a.equivalentWalkingMeters / 1000).toFixed(1)}<small>km</small></span>
                </div>
              </div>

              <div className="muted" style={{ fontSize: '.78rem', marginBottom: 6 }}>Active calories · target progress</div>
              <div className="progress" aria-label={`${a.activeCalories} of ${a.targetCalories} active calories`}>
                <i style={{ width: `${a.targetCalories > 0 ? Math.min(100, (a.activeCalories / a.targetCalories) * 100) : 0}%` }} />
              </div>

              <div style={{ marginTop: 16 }}>
                <div className="muted" style={{ fontSize: '.78rem', marginBottom: 6 }}>Day composition</div>
                <ActivityComposition a={a} />
              </div>
            </>
          )}
        </Ready>

        <div style={{ marginTop: 18 }}>
          <div className="muted" style={{ fontSize: '.78rem', marginBottom: 6 }}>Steps · last 7 days</div>
          <Ready s={activityWeek} loading={<SkeletonBars rows={7} />}>
            {days => {
              const last7 = days.slice(-7)
              if (last7.length === 0) return <span className="muted">No data</span>
              const max = Math.max(1, ...last7.map(d => d.steps))
              return (
                <div className="stack-xs" role="list">
                  {last7.map(d => (
                    <div key={d.date} className="metric-row" role="listitem" aria-label={`${formatShortDate(d.date)}: ${d.steps.toLocaleString()} steps`}>
                      <div className="label">{formatShortDate(d.date)}</div>
                      <div className="bar" aria-hidden><i style={{ width: `${(d.steps / max) * 100}%` }} /></div>
                      <div className="value">{(d.steps/1000).toFixed(1)}k</div>
                    </div>
                  ))}
                </div>
              )
            }}
          </Ready>
        </div>
      </section>

      {/* --- heart & HRV section ---------------------------------------- */}
      <section className="card" aria-label="Heart rate and HRV">
        <div className="section-head"><h3>Heart &amp; HRV</h3><span className="sub">nightly trend, last 14 nights</span></div>
        <Ready s={nightlyHR} loading={<SkeletonLines lines={4} />}>
          {nights => {
            const withData = nights.filter(n => n.lowestHeartRate > 0 || n.averageHrv > 0)
            if (withData.length === 0) return <span className="muted">No data</span>
            return (
              <div className="grid grid-2" style={{ gap: 16 }}>
                <MiniLine title="Resting HR (lowest)" unit="bpm" color="#FFA66B"
                          data={withData.map(n => n.lowestHeartRate)} labels={withData.map(n => n.day)} />
                <MiniLine title="HRV (avg)" unit="ms" color="#8CF7E6"
                          data={withData.map(n => n.averageHrv)} labels={withData.map(n => n.day)} />
              </div>
            )
          }}
        </Ready>

        <div style={{ marginTop: 18 }}>
          <div className="muted" style={{ fontSize: '.78rem', marginBottom: 6 }}>Heart rate · last 48h</div>
          <Ready s={hrDay} loading={<div className="skeleton" style={{ height: 140 }} />}>
            {pts => pts.length === 0
              ? <span className="muted">No data</span>
              : <LineChart data={pts.map(p => p.bpm)} labels={pts.map(p => p.time)} unit="bpm" timeZone="local" height={140} color="#FFA66B" />
            }
          </Ready>
        </div>
      </section>

      {/* --- body & resilience ------------------------------------------ */}
      <section className="card" aria-label="Body markers and resilience">
        <div className="section-head"><h3>Body &amp; resilience</h3><span className="sub">long-term markers</span></div>
        <Ready s={body} loading={<SkeletonLines lines={3} />}>
          {b => {
            const delta = b.vascularAge - b.age
            const deltaClass = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
            return (
              <div className="stat-row">
                <div className="stat">
                  <span className="k">Cardiovascular age</span>
                  <span className="v">
                    {b.vascularAge || '—'}<small>yrs</small>
                    {b.vascularAge > 0 && (
                      <span className={`delta ${deltaClass}`}>
                        {delta > 0 ? `+${delta}` : delta === 0 ? '±0' : delta} vs {b.age}
                      </span>
                    )}
                  </span>
                </div>
                <div className="stat">
                  <span className="k">Resilience</span>
                  <span className="v"><ResilienceChip level={b.resilienceLevel} /></span>
                </div>
                <div className="stat">
                  <span className="k">Breathing disturbance</span>
                  <span className="v">{b.breathingDisturbanceIndex || '—'}<small>/ night</small></span>
                </div>
                <div className="stat">
                  <span className="k">Pulse wave velocity</span>
                  <span className="v">{b.pulseWaveVelocity ? b.pulseWaveVelocity.toFixed(2) : '—'}<small>m/s</small></span>
                </div>
              </div>
            )
          }}
        </Ready>
      </section>

      {/* --- recent workouts -------------------------------------------- */}
      <section className="card" aria-label="Recent workouts">
        <div className="section-head"><h3>Recent workouts</h3><span className="sub">last 30 days</span></div>
        <Ready s={workouts} loading={<SkeletonLines lines={4} />}>
          {ws => ws.length === 0
            ? <span className="muted">No workouts</span>
            : (
              <table className="wtable">
                <thead>
                  <tr><th>Day</th><th>Activity</th><th>Intensity</th><th>Duration</th><th>Distance</th><th style={{ textAlign: 'right' }}>Calories</th></tr>
                </thead>
                <tbody>
                  {ws.map((w, i) => (
                    <tr key={i}>
                      <td>{formatShortDate(w.day)}</td>
                      <td style={{ textTransform: 'capitalize' }}>{w.activity.replace(/_/g, ' ')}</td>
                      <td><IntensityChip intensity={w.intensity} /></td>
                      <td>{formatDuration(w.durationSeconds)}</td>
                      <td>{w.distanceMeters > 0 ? `${(w.distanceMeters/1000).toFixed(2)} km` : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{w.calories}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </Ready>
      </section>
    </div>
  )
}

// -- small components --------------------------------------------------------

function Ready<T>({ s, children, loading }: { s: Status<T>, children: (data: T) => React.ReactNode, loading?: React.ReactNode }){
  if (s.kind === 'loading') return <>{loading ?? <SkeletonLines lines={1} />}</>
  if (s.kind === 'error') return <span className="muted">Couldn't load</span>
  return <>{children(s.data)}</>
}

function SkeletonLines({ lines }: { lines: number }){
  return (
    <div className="stack-xs">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="skeleton" style={{ width: i === 0 ? '55%' : '80%' }} />
      ))}
    </div>
  )
}

function SkeletonBars({ rows }: { rows: number }){
  return (
    <div className="stack-xs">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="metric-row">
          <div className="skeleton" style={{ height: 10 }} />
          <div className="skeleton" style={{ height: 10 }} />
          <div className="skeleton" style={{ height: 10 }} />
        </div>
      ))}
    </div>
  )
}

function ScoreTile({ eyebrow, trend, latest, extra }: { eyebrow: string, trend: Status<DailyScore[]>, latest: number | null, extra?: string }){
  const color = scoreColor(latest)
  return (
    <div className="tile" aria-label={`${eyebrow} score`}>
      <div className="eyebrow">{eyebrow}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span className="score" style={{ color }}>{latest ?? '—'}</span>
        <span className="sub">{scoreLabel(latest)}</span>
      </div>
      {extra && <div className="sub">{extra}</div>}
      <div className="trend">
        {trend.kind === 'ok'
          ? <Sparkline data={trend.data.map(d => d.score)} color={color} />
          : <div className="skeleton" style={{ height: 28 }} />
        }
      </div>
    </div>
  )
}

function StateChip({ state }: { state: string }){
  const t = tonesFor(state)
  return (
    <span className="chip" style={{ background: t.bg, color: t.fg, borderColor: t.border }}>
      <span className="dot" />{state === 'unknown' ? 'unknown' : state}
    </span>
  )
}

function ResilienceChip({ level }: { level: string }){
  if (!level) return <span className="muted">—</span>
  const m: Record<string, { fg: string, bg: string }> = {
    limited:     { fg: '#FF7A66', bg: '#3a1e1a' },
    adequate:    { fg: '#FFCB6B', bg: '#3a2f18' },
    solid:       { fg: '#8CF7E6', bg: '#10302b' },
    strong:      { fg: '#2EE1B9', bg: '#04251E' },
    exceptional: { fg: '#2EE1B9', bg: '#04251E' },
  }
  const c = m[level] || { fg: '#9fb5b0', bg: '#132522' }
  return <span className="level-chip" style={{ color: c.fg, background: c.bg, borderColor: `${c.fg}55` }}>{level}</span>
}

function IntensityChip({ intensity }: { intensity: string }){
  const m: Record<string, string> = { easy: '#9fb5b0', moderate: '#2EE1B9', vigorous: '#FFCB6B', high: '#FF7A66' }
  const c = m[intensity] || '#9fb5b0'
  return <span className="level-chip" style={{ color: c, background: `${c}1a`, borderColor: `${c}55` }}>{intensity}</span>
}

function StageBar({ night }: { night: SleepNight }){
  const stages = [
    { label: 'Deep',  min: night.deepSleepMinutes,  color: '#1FA47F' },
    { label: 'REM',   min: night.remSleepMinutes,   color: '#2EE1B9' },
    { label: 'Light', min: night.lightSleepMinutes, color: '#8CF7E6' },
    { label: 'Awake', min: night.awakeMinutes,      color: '#3A4D48' },
  ]
  const total = stages.reduce((s, x) => s + x.min, 0) || 1
  return (
    <>
      <div className="stack-bar" role="img" aria-label={`Sleep stages: deep ${night.deepSleepMinutes}m, REM ${night.remSleepMinutes}m, light ${night.lightSleepMinutes}m, awake ${night.awakeMinutes}m`}>
        {stages.map(s => <span key={s.label} style={{ background: s.color, width: `${(s.min/total)*100}%` }} />)}
      </div>
      <div className="seg-legend">
        {stages.map(s => (
          <span key={s.label}>
            <span className="dot" style={{ background: s.color }} />{s.label} {fmtHM(s.min)}
          </span>
        ))}
      </div>
    </>
  )
}

function ActivityComposition({ a }: { a: ActivityDay }){
  // Use a fixed day length so absolute-length reads truthfully; non-wear time is implicit gap.
  const DAY = 24 * 60
  const segs = [
    { label: 'Resting',   min: a.restingMinutes,        color: '#1a2a26' },
    { label: 'Sedentary', min: a.sedentaryMinutes,      color: '#2d3b37' },
    { label: 'Low',       min: a.lowActivityMinutes,    color: '#2EE1B9' },
    { label: 'Medium',    min: a.mediumActivityMinutes, color: '#FFCB6B' },
    { label: 'High',      min: a.highActivityMinutes,   color: '#FF7A66' },
  ]
  const used = segs.reduce((s,x) => s + x.min, 0)
  const gap = Math.max(0, DAY - used)
  return (
    <>
      <div className="stack-bar" role="img" aria-label="Activity composition for the day">
        {segs.map(s => s.min > 0 && <span key={s.label} style={{ background: s.color, width: `${(s.min/DAY)*100}%` }} />)}
        {gap > 0 && <span style={{ background: 'transparent', width: `${(gap/DAY)*100}%` }} />}
      </div>
      <div className="seg-legend">
        {segs.map(s => (
          <span key={s.label}>
            <span className="dot" style={{ background: s.color }} />{s.label} {fmtHM(s.min)}
          </span>
        ))}
      </div>
    </>
  )
}

function SleepWeekList({ days, avgMinutes }: { days: { date: string, total_sleep_minutes: number }[], avgMinutes: number }){
  const capMin = 10 * 60
  const targetMin = 8 * 60
  return (
    <>
      <div className="stack-xs" role="list">
        {days.map(d => {
          const hours = d.total_sleep_minutes / 60
          return (
            <div key={d.date} className="metric-row" role="listitem" aria-label={`${formatShortDate(d.date)}: ${hours.toFixed(1)} hours`}>
              <div className="label">{formatShortDate(d.date)}</div>
              <div className="bar alt" aria-hidden>
                <i style={{ width: `${Math.min(100, (d.total_sleep_minutes / capMin) * 100)}%` }} />
                <div className="marker" style={{ left: `${(targetMin / capMin) * 100}%` }} />
              </div>
              <div className="value">{hours.toFixed(1)}h</div>
            </div>
          )
        })}
      </div>
      <small className="card-sub">7-day avg: {(avgMinutes/60).toFixed(1)} h · target 8 h</small>
    </>
  )
}

// -- charts ------------------------------------------------------------------

function Sparkline({ data, color }: { data: number[], color: string }){
  const W = 120, H = 32, P = 2
  const { poly, area } = useMemo(() => {
    if (!data.length) return { poly: '', area: '' }
    const mi = Math.min(...data), ma = Math.max(...data)
    const span = (ma - mi) || 1
    const pts = data.map((v, i) => {
      const x = P + (i * (W - 2*P)) / Math.max(1, data.length-1)
      const y = P + (H - 2*P) - ((v - mi) * (H - 2*P)) / span
      return [x, y]
    })
    const poly = pts.map(p => p.join(',')).join(' ')
    const area = `${P},${H-P} ${poly} ${W-P},${H-P}`
    return { poly, area }
  }, [data])
  if (!poly) return <div className="muted" style={{ fontSize: '.75rem' }}>—</div>
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" aria-hidden>
      <polygon fill={`${color}22`} points={area} />
      <polyline fill="none" stroke={color} strokeWidth={1.5} points={poly} />
    </svg>
  )
}

function MiniLine({ title, unit, color, data, labels }: { title: string, unit: string, color: string, data: number[], labels: string[] }){
  if (data.length === 0) return <div><div className="muted" style={{ fontSize: '.8rem', marginBottom: 4 }}>{title}</div><span className="muted">—</span></div>
  const latest = data[data.length - 1]
  const prev = data.length > 1 ? data[data.length - 2] : latest
  const delta = latest - prev
  const deltaClass = Math.abs(delta) < 0.5 ? 'flat' : delta > 0 ? 'up' : 'down'
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span className="muted" style={{ fontSize: '.8rem' }}>{title}</span>
        <span style={{ fontSize: '.9rem' }}>
          {latest.toFixed(0)}<small className="muted" style={{ marginLeft: 3 }}>{unit}</small>
          <span className={`delta ${deltaClass}`}>{delta > 0 ? '+' : ''}{delta.toFixed(0)}</span>
        </span>
      </div>
      <LineChart data={data} labels={labels} unit={unit} height={80} color={color} />
    </div>
  )
}

function LineChart({ data, labels, unit, timeZone = 'local', width=560, height=100, color='#2EE1B9' }: { data: number[], labels?: string[], unit?: string, timeZone?: 'local' | 'utc', width?: number, height?: number, color?: string }){
  const padding = 8
  const [hover, setHover] = useState<number | null>(null)
  const { points, min, max, coords } = useMemo(()=>{
    if (!data || data.length === 0) return { points: '', min: 0, max: 0, coords: [] as {x:number,y:number,val:number}[] }
    const min = Math.min(...data)
    const max = Math.max(...data)
    const span = (max - min) || 1
    const coords = data.map((v, i)=>{
      const x = padding + (i*(width-2*padding))/Math.max(1, data.length-1)
      const y = padding + (height-2*padding) - ((v - min)*(height-2*padding))/span
      return { x: +x.toFixed(1), y: +y.toFixed(1), val: v }
    })
    return { points: coords.map(p=>`${p.x},${p.y}`).join(' '), min, max, coords }
  }, [data, width, height])
  if (!points) return <div><small>No data</small></div>

  const midY = (min + max) / 2
  const yFor = (v: number) => { const span = (max - min) || 1; return padding + (height-2*padding) - ((v - min)*(height-2*padding))/span }
  const xCount = Math.max(1, (data?.length || 0) - 1)
  const xForIndex = (i: number) => padding + (i*(width-2*padding))/Math.max(1, (data?.length || 0)-1)
  const xLabels = [0, Math.floor(xCount/2), xCount]

  const fmtX = (i: number) => {
    if (!labels || labels.length === 0) return (i+1).toString()
    const raw = labels[Math.max(0, Math.min(labels.length-1, i))]
    if (DATE_ONLY.test(raw)) return formatShortDate(raw)
    const t = new Date(raw)
    if (!isNaN(t.getTime())){
      const o: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
      if (timeZone === 'utc') (o as any).timeZone = 'UTC'
      return t.toLocaleTimeString([], o)
    }
    return String(raw)
  }
  const fmtHoverX = (i: number) => {
    if (!labels || labels.length === 0) return ''
    const raw = labels[Math.max(0, Math.min(labels.length-1, i))]
    if (DATE_ONLY.test(raw)) return formatShortDate(raw)
    const t = new Date(raw)
    if (!isNaN(t.getTime())){
      const o: Intl.DateTimeFormatOptions = { month:'short', day:'numeric', hour: '2-digit', minute: '2-digit' }
      if (timeZone === 'utc') (o as any).timeZone = 'UTC'
      return t.toLocaleString([], o)
    }
    return String(raw)
  }

  const onMove: React.MouseEventHandler<SVGRectElement> = (e) => {
    const svg = e.currentTarget.ownerSVGElement as SVGSVGElement | null
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const mx = ((e.clientX - rect.left) / rect.width) * width
    let idx = 0, best = Infinity
    for (let i=0;i<coords.length;i++){
      const d = Math.abs(coords[i].x - mx)
      if (d < best){ best = d; idx = i }
    }
    setHover(idx)
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label={`Line chart, ${data.length} points, ${Math.round(min)}-${Math.round(max)}${unit ? ' ' + unit : ''}`} style={{ display:'block', background:'var(--surface-2)', border:'1px solid #12241F', borderRadius:6 }}>
      <g stroke="#1b2a26" strokeWidth={1}>
        <line x1={padding} y1={height-padding} x2={width-padding} y2={height-padding} />
        <line x1={padding} y1={padding} x2={padding} y2={height-padding} />
        <line x1={padding} y1={yFor(min)} x2={width-padding} y2={yFor(min)} opacity={0.3} />
        <line x1={padding} y1={yFor(midY)} x2={width-padding} y2={yFor(midY)} opacity={0.3} />
        <line x1={padding} y1={yFor(max)} x2={width-padding} y2={yFor(max)} opacity={0.3} />
      </g>
      <g fontSize="10" fill="#9fb5b0">
        <text x={padding+2} y={yFor(max)-2}>{Math.round(max)}</text>
        <text x={padding+2} y={yFor(midY)-2}>{Math.round(midY)}</text>
        <text x={padding+2} y={yFor(min)-2}>{Math.round(min)}</text>
      </g>
      <g fontSize="10" fill="#9fb5b0" textAnchor="middle">
        {xLabels.map((i, idx)=> <text key={idx} x={xForIndex(i)} y={height-2}>{fmtX(i)}</text>)}
      </g>
      <polyline fill="none" stroke={color} strokeWidth={2} points={points} />
      <rect x={0} y={0} width={width} height={height} fill="transparent" onMouseMove={onMove} onMouseLeave={() => setHover(null)} />
      {hover != null && coords[hover] && (
        <g style={{ pointerEvents:'none' }}>
          <line x1={coords[hover].x} y1={padding} x2={coords[hover].x} y2={height-padding} stroke="#2a3e39" strokeWidth={1} />
          <circle cx={coords[hover].x} cy={coords[hover].y} r={3} fill={color} stroke="#0e1512" strokeWidth={1} />
          {(() => {
            const boxW = 130
            const hasTime = !!labels && labels.length > 0
            const boxH = hasTime ? 34 : 20
            const tx = Math.min(width - padding - boxW, coords[hover].x + 8)
            const ty = Math.max(padding + boxH, coords[hover].y) - boxH
            return (
              <g>
                <rect x={tx} y={ty} rx={4} ry={4} width={boxW} height={boxH} fill="#0e1512" stroke="#2a3e39" />
                <text x={tx+6} y={ty+13} fontSize="11" fill="#d9ebe7">{Math.round(coords[hover].val)}{unit ? ` ${unit}` : ''}</text>
                {hasTime && (<text x={tx+6} y={ty+25} fontSize="10" fill="#9fb5b0">{fmtHoverX(hover)}</text>)}
              </g>
            )
          })()}
        </g>
      )}
    </svg>
  )
}

// -- helpers -----------------------------------------------------------------

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

function formatShortDate(iso: string){
  if (!iso) return ''
  const s = iso.length > 10 ? iso.slice(0,10) : iso
  if (!DATE_ONLY.test(s)) return s
  const [y,m,d] = s.split('-').map(Number)
  return new Date(y, m-1, d).toLocaleDateString([], { weekday: 'short', day: 'numeric' })
}

function formatTime(iso: string){
  if (!iso) return ''
  const t = new Date(iso)
  if (isNaN(t.getTime())) return ''
  return t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDuration(seconds: number){
  if (!seconds || seconds <= 0) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function fmtHM(minutes: number){
  if (!minutes || minutes <= 0) return '0m'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function scoreColor(score: number | null): string {
  if (score == null) return '#9fb5b0'
  if (score >= 85) return '#2EE1B9'
  if (score >= 70) return '#8CF7E6'
  if (score >= 55) return '#FFCB6B'
  return '#FF7A66'
}
function scoreLabel(score: number | null): string {
  if (score == null) return 'no data'
  if (score >= 85) return 'great'
  if (score >= 70) return 'good'
  if (score >= 55) return 'ok'
  return 'low'
}

function latestScore(s: Status<DailyScore[]>): number | null {
  if (s.kind !== 'ok' || s.data.length === 0) return null
  return s.data[s.data.length - 1].score
}
function activityWeekScoreSeries(s: Status<ActivityDay[]>): Status<DailyScore[]> {
  if (s.kind !== 'ok') return s as any
  return { kind: 'ok', data: s.data.map(d => ({ date: d.date, score: d.score })) }
}
function latestActivityScore(s: Status<ActivityDay[]>): number | null {
  if (s.kind !== 'ok' || s.data.length === 0) return null
  return s.data[s.data.length - 1].score
}

function tonesFor(state: string){
  switch (state){
    case 'stressed':   return { bg: '#3a1e1a', fg: '#FFA66B', border: 'rgba(255,166,107,.35)' }
    case 'recovering': return { bg: '#102a26', fg: '#8CF7E6', border: 'rgba(140,247,230,.35)' }
    case 'steady':     return { bg: 'var(--accent-contrast)', fg: 'var(--accent)', border: 'rgba(46,225,185,.35)' }
    default:           return { bg: '#132522', fg: '#9fb5b0', border: '#1b302b' }
  }
}
